import { useMemo } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { notifications } from '@mantine/notifications';
import { productsApi } from '../api/products.api';
import type { Product, ProductCardResponse, CreateProductDto, SetDiscountDto, FlowerType, AssemblyType, Color } from '../types';

export interface ProductFilters {
  categoryId?: number;
  colorIds?: number[];
  flowerTypeIds?: number[];
  assemblyTypeIds?: number[];
  minPrice?: number;
  maxPrice?: number;
  sortBy?: string;
  /** Админ: id продавца */
  sellerId?: number;
  search?: string;
  moderation?: 'all' | 'pending' | 'approved' | 'rejected';
  ratings?: number[];
  badges?: Array<'promo' | 'sale' | 'popular' | 'new' | 'hidden' | 'oos'>;
  page?: number;
  limit?: number;
}

export const useProducts = (filters?: ProductFilters) => {
  const query = useQuery<ProductCardResponse[] | { products: ProductCardResponse[]; total: number }>({
    queryKey: ['products', 'list', filters],
    queryFn: () => productsApi.getAll(filters),
    placeholderData: (previousData) => previousData,
  });
  const products = useMemo(() => {
    const data = query.data;
    if (data == null) return [];
    return Array.isArray(data) ? data : data.products;
  }, [query.data]);
  const total = useMemo(() => {
    const data = query.data;
    if (data == null) return 0;
    return Array.isArray(data) ? data.length : data.total;
  }, [query.data]);
  return { ...query, data: products, total };
};

export const useFlowerTypes = () => {
  return useQuery<FlowerType[]>({
    queryKey: ['products', 'options', 'flower-types'],
    queryFn: () => productsApi.getFlowerTypes(),
  });
};

export const useAssemblyTypes = () => {
  return useQuery<AssemblyType[]>({
    queryKey: ['products', 'options', 'assembly-types'],
    queryFn: () => productsApi.getAssemblyTypes(),
  });
};

export const useColors = () => {
  return useQuery<Color[]>({
    queryKey: ['products', 'options', 'colors'],
    queryFn: () => productsApi.getColors(),
  });
};

// Хуки для получения готовых опций для селектов
export const useFlowerTypeOptions = () => {
  const { data: flowerTypes = [] } = useFlowerTypes();

  return useMemo(() => {
    return flowerTypes.map(ft => ({
      value: ft.id.toString(),
      label: ft.name,
    }));
  }, [flowerTypes]);
};

export const useAssemblyTypeOptions = () => {
  const { data: assemblyTypes = [] } = useAssemblyTypes();

  return useMemo(() => {
    return assemblyTypes.map(at => ({
      value: at.id.toString(),
      label: at.name,
    }));
  }, [assemblyTypes]);
};

export const useColorOptions = () => {
  const { data: colors = [] } = useColors();

  return useMemo(() => {
    return colors.map(c => ({
      value: c.id.toString(),
      label: c.name,
    }));
  }, [colors]);
};

export const useProduct = (id: number) => {
  return useQuery<Product>({
    queryKey: ['product', id],
    queryFn: () => productsApi.getById(id),
    enabled: !!id,
  });
};

export const useProductsModerationPendingCount = (enabled = true) => {
  return useQuery<{ count: number }>({
    queryKey: ['products', 'moderation', 'pending-count'],
    queryFn: () => productsApi.getModerationPendingCount(),
    enabled,
  });
};

export const useSetProductApproval = () => {
  const queryClient = useQueryClient();

  return useMutation<Product, Error, { id: number; approved: boolean; reason?: string }>({
    mutationFn: ({ id, approved, reason }) => productsApi.setApproval(id, approved, reason),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ['products', 'list'] });
      queryClient.invalidateQueries({ queryKey: ['product', variables.id] });
      queryClient.invalidateQueries({ queryKey: ['products', 'collections'] });
      queryClient.invalidateQueries({ queryKey: ['products', 'moderation', 'pending-count'] });
    },
  });
};

const REJECTION_TEMPLATES_QUERY_KEY = ['products', 'rejection-templates'] as const;

export const useProductRejectionTemplates = () =>
  useQuery({
    queryKey: REJECTION_TEMPLATES_QUERY_KEY,
    queryFn: productsApi.getRejectionTemplates,
  });

export const useCreateProductRejectionTemplate = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: productsApi.createRejectionTemplate,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: REJECTION_TEMPLATES_QUERY_KEY });
    },
  });
};

export const useDeleteProductRejectionTemplate = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: productsApi.deleteRejectionTemplate,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: REJECTION_TEMPLATES_QUERY_KEY });
    },
  });
};

export const useUpdateProduct = () => {
  const queryClient = useQueryClient();

  return useMutation<Product, Error, { id: number; data: Partial<CreateProductDto>; files?: File[]; deletedImageIds?: number[] }>({
    mutationFn: ({ id, data, files, deletedImageIds }) => productsApi.update(id, data, files, deletedImageIds),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ['products', 'list'] });
      queryClient.invalidateQueries({ queryKey: ['product', variables.id] });
      queryClient.invalidateQueries({ queryKey: ['products', 'collections'] });
    },
  });
};

export const useReorderProductImages = (productId: number) => {
  const queryClient = useQueryClient();

  return useMutation<Product, Error, { id: number; order: number }[]>({
    mutationFn: (items) => productsApi.updateProductImagesOrder(productId, items),
    onSuccess: (data) => {
      queryClient.setQueryData(['product', productId], (prev: Product | undefined) =>
        prev ? { ...prev, images: data.images } : prev,
      );
    },
    onError: () => {
      queryClient.invalidateQueries({ queryKey: ['product', productId] });
      notifications.show({
        title: 'Ошибка',
        message: 'Не удалось изменить порядок изображений',
        color: 'red',
      });
    },
  });
};

export const useSetProductDiscount = () => {
  const queryClient = useQueryClient();

  return useMutation<Product, Error, { id: number; data: SetDiscountDto }>({
    mutationFn: ({ id, data }) => productsApi.setDiscount(id, data),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ['products', 'list'] });
      queryClient.invalidateQueries({ queryKey: ['product', variables.id] });
      queryClient.invalidateQueries({ queryKey: ['products', 'collections', 'discount'] });
      queryClient.invalidateQueries({ queryKey: ['products', 'collections'] });
    },
  });
};

export const useSetProductFlag = (type: string) => {
  const queryClient = useQueryClient();

  return useMutation<Product, Error, { id: number; value: boolean }>({
    mutationFn: ({ id, value }) => productsApi.setFlag(id, type, value),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ['products', 'list'] });
      queryClient.invalidateQueries({ queryKey: ['product', variables.id] });
      queryClient.invalidateQueries({ queryKey: ['products', 'collections', type] });
      queryClient.invalidateQueries({ queryKey: ['products', 'collections'] });
    },
  });
};


export const useDeleteProduct = () => {
  const queryClient = useQueryClient();

  return useMutation<void, Error, number>({
    mutationFn: (id) => productsApi.delete(id),
    onSuccess: (_, id) => {
      // Инвалидируем все списки продуктов
      queryClient.invalidateQueries({ queryKey: ['products', 'list'] });
      // Инвалидируем все коллекции продуктов
      queryClient.invalidateQueries({ queryKey: ['products', 'collections'] });
      // Инвалидируем список подборок для обновления информации о подборках
      queryClient.invalidateQueries({ queryKey: ['customSets'] });
      // Удаляем кэш для удаленного товара
      queryClient.removeQueries({ queryKey: ['product', id] });
      // Примечание: конкретные подборки инвалидируются в компоненте на основе данных товара
    },
  });
};

export const usePopularProducts = () => {
  return useQuery<ProductCardResponse[]>({
    queryKey: ['products', 'collections', 'popular'],
    queryFn: () => productsApi.getPopular(),
  });
};

export const useHiddenProducts = () => {
  return useQuery<ProductCardResponse[]>({
    queryKey: ['products', 'collections', 'hidden'],
    queryFn: () => productsApi.getHidden(),
  });
};

export const useDiscountProducts = () => {
  return useQuery<ProductCardResponse[]>({
    queryKey: ['products', 'collections', 'discount'],
    queryFn: () => productsApi.getDiscount(),
  });
};

export const useOutOfStockProducts = () => {
  return useQuery<ProductCardResponse[]>({
    queryKey: ['products', 'collections', 'out-of-stock'],
    queryFn: () => productsApi.getOutOfStock(),
  });
};

export const useNewProducts = () => {
  return useQuery<ProductCardResponse[]>({
    queryKey: ['products', 'collections', 'new'],
    queryFn: () => productsApi.getNew(),
  });
};

// Массовые операции
export const useBulkSetFlag = (type: string) => {
  const queryClient = useQueryClient();

  return useMutation<{ count: number }, Error, { productIds: number[]; value: boolean }>({
    mutationFn: ({ productIds, value }) => productsApi.bulkSetFlag(type, productIds, value),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ['products', 'list'] });
      queryClient.invalidateQueries({ queryKey: ['products', 'collections', type] });
      queryClient.invalidateQueries({ queryKey: ['products', 'collections'] });
      // Инвалидируем кэш для каждого обновленного продукта
      variables.productIds.forEach(productId => {
        queryClient.invalidateQueries({ queryKey: ['product', productId] });
      });
    },
  });
};

export const useBulkDeleteProducts = () => {
  const queryClient = useQueryClient();

  return useMutation<void, Error, { productIds: number[] }>({
    mutationFn: ({ productIds }) => productsApi.bulkDelete(productIds),
    onSuccess: (_, variables) => {
      // Инвалидируем все списки продуктов
      queryClient.invalidateQueries({ queryKey: ['products', 'list'] });
      // Инвалидируем все коллекции продуктов
      queryClient.invalidateQueries({ queryKey: ['products', 'collections'] });
      // Инвалидируем все кастомные подборки (так как мы не знаем, в каких именно были товары)
      queryClient.invalidateQueries({ queryKey: ['customSetProducts'] });
      queryClient.invalidateQueries({ queryKey: ['customSet'] });
      // Инвалидируем список подборок для обновления информации о подборках
      queryClient.invalidateQueries({ queryKey: ['customSets'] });
      // Удаляем кеш для удаленных товаров
      variables.productIds.forEach(id => {
        queryClient.removeQueries({ queryKey: ['product', id] });
      });
    },
  });
};

export const useBulkAddToCategory = () => {
  const queryClient = useQueryClient();

  return useMutation<{ success: boolean; count: number }, Error, { productIds: number[]; categoryId: number }>({
    mutationFn: ({ productIds, categoryId }) => productsApi.bulkAddToCategory(productIds, categoryId),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ['products', 'list'] });
      // Инвалидируем кеш для обновленных товаров
      variables.productIds.forEach(id => {
        queryClient.invalidateQueries({ queryKey: ['product', id] });
      });
    },
  });
};

export const useRemoveFromCategory = () => {
  const queryClient = useQueryClient();

  return useMutation<{ success: boolean }, Error, { productId: number; categoryId: number }>({
    mutationFn: ({ productId, categoryId }) => productsApi.removeFromCategory(productId, categoryId),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ['products', 'list'] });
      queryClient.invalidateQueries({ queryKey: ['product', variables.productId] });
    },
  });
};

// Color mutations
export const useCreateColor = () => {
  const queryClient = useQueryClient();

  return useMutation<Color, Error, { name: string; hexCode?: string }>({
    mutationFn: (data) => productsApi.createColor(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['products', 'options', 'colors'] });
    },
  });
};

export const useUpdateColor = () => {
  const queryClient = useQueryClient();

  return useMutation<Color, Error, { id: number; data: { name?: string; hexCode?: string } }>({
    mutationFn: ({ id, data }) => productsApi.updateColor(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['products', 'options', 'colors'] });
    },
  });
};

export const useDeleteColor = () => {
  const queryClient = useQueryClient();

  return useMutation<void, Error, number>({
    mutationFn: (id) => productsApi.deleteColor(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['products', 'options', 'colors'] });
    },
  });
};

// FlowerType mutations
export const useCreateFlowerType = () => {
  const queryClient = useQueryClient();

  return useMutation<FlowerType, Error, { name: string }>({
    mutationFn: (data) => productsApi.createFlowerType(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['products', 'options', 'flower-types'] });
    },
  });
};

export const useUpdateFlowerType = () => {
  const queryClient = useQueryClient();

  return useMutation<FlowerType, Error, { id: number; data: { name?: string } }>({
    mutationFn: ({ id, data }) => productsApi.updateFlowerType(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['products', 'options', 'flower-types'] });
    },
  });
};

export const useDeleteFlowerType = () => {
  const queryClient = useQueryClient();

  return useMutation<void, Error, number>({
    mutationFn: (id) => productsApi.deleteFlowerType(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['products', 'options', 'flower-types'] });
    },
  });
};

// AssemblyType mutations
export const useCreateAssemblyType = () => {
  const queryClient = useQueryClient();

  return useMutation<AssemblyType, Error, { name: string }>({
    mutationFn: (data) => productsApi.createAssemblyType(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['products', 'options', 'assembly-types'] });
    },
  });
};

export const useUpdateAssemblyType = () => {
  const queryClient = useQueryClient();

  return useMutation<AssemblyType, Error, { id: number; data: { name?: string } }>({
    mutationFn: ({ id, data }) => productsApi.updateAssemblyType(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['products', 'options', 'assembly-types'] });
    },
  });
};

export const useDeleteAssemblyType = () => {
  const queryClient = useQueryClient();

  return useMutation<void, Error, number>({
    mutationFn: (id) => productsApi.deleteAssemblyType(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['products', 'options', 'assembly-types'] });
    },
  });
};

export const useCreateVariant = () => {
  const queryClient = useQueryClient();

  return useMutation<
    { success: boolean; variantId: number; message: string },
    Error,
    { products: Array<{ productId: number }> }
  >({
    mutationFn: (data) => productsApi.createVariant(data),
    onSuccess: (_, variables) => {
      // Инвалидируем кэш для всех товаров в варианте
      variables.products.forEach(product => {
        queryClient.invalidateQueries({ queryKey: ['product', product.productId] });
      });
      queryClient.invalidateQueries({ queryKey: ['products', 'list'] });
    },
  });
};

export const useUpdateVariantName = () => {
  const queryClient = useQueryClient();

  return useMutation<
    { success: boolean; message: string },
    Error,
    { productId: number; variantName: string }
  >({
    mutationFn: ({ productId, variantName }) => productsApi.updateVariantName(productId, variantName),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ['product', variables.productId] });
      queryClient.invalidateQueries({ queryKey: ['products', 'list'] });
    },
  });
};

export const useBulkUpdateVariantName = () => {
  const queryClient = useQueryClient();

  return useMutation<
    { success: boolean; count: number; total: number; results: Array<{ productId: number; success: boolean; message: string }>; message: string },
    Error,
    { updates: Array<{ productId: number; variantName: string }> }
  >({
    mutationFn: ({ updates }) => productsApi.bulkUpdateVariantName(updates),
    onSuccess: (_, variables) => {
      // Инвалидируем кэш для всех обновленных товаров
      variables.updates.forEach(update => {
        queryClient.invalidateQueries({ queryKey: ['product', update.productId] });
      });
      queryClient.invalidateQueries({ queryKey: ['products', 'list'] });
    },
  });
};

export const useRemoveVariant = () => {
  const queryClient = useQueryClient();

  return useMutation<
    { success: boolean; message: string },
    Error,
    { variantId: number; productId: number }
  >({
    mutationFn: ({ variantId }) => productsApi.removeVariant(variantId),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ['product', variables.productId] });
      queryClient.invalidateQueries({ queryKey: ['products', 'list'] });
    },
  });
};

export const useDisbandVariantGroup = () => {
  const queryClient = useQueryClient();

  return useMutation<
    { success: boolean; message: string },
    Error,
    number
  >({
    mutationFn: (productId) => productsApi.disbandVariantGroup(productId),
    onSuccess: (_, productId) => {
      queryClient.invalidateQueries({ queryKey: ['product', productId] });
      queryClient.invalidateQueries({ queryKey: ['products', 'list'] });
    },
  });
};

